import { computed, ref, Ref } from 'vue';
import { VisibilityState } from '@tanstack/vue-table';
import { isEqual, merge } from 'lodash-es';
import { useEnvStore } from '../core/useEnvStore';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridColumnInstance,
  GridColumnVisibilityOption,
  ColumnVisibilityInfo,
} from '../../modernGrid.types';
import { getDefaultColumnVisibilityOption } from '../../modernGrid.define';
import { existObjectValue, isChangedEnv } from '../../modernGrid.utils';

export const useColumnVisibility = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  columnSettingList,
  envKey,
  hideLastColumn,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  columnSettingList: Ref<ColumnVisibilityInfo[]>;
  envKey: Ref<string | undefined>;
  hideLastColumn: () => Promise<void>;
}) => {
  const { getEnv, saveEnv } = useEnvStore(props.useDashboardStore ?? false);
  const columnVisibilityInfo = computed<GridColumnVisibilityOption>(() =>
    merge({}, getDefaultColumnVisibilityOption(), props.option?.columnVisibility),
  );
  const columnVisibilityState = ref<VisibilityState>({});

  const setColumnVisibility = async (
    columnVisibilitySettings: ColumnVisibilityInfo[],
  ): Promise<void> => {
    // 마지막 컬럼 드래깅 중인 경우 렌더링 문제 방지를 위해 먼저 숨김
    await hideLastColumn();

    const currentVisibility = { ...columnVisibilityState.value };
    columnVisibilitySettings.forEach((info) => {
      currentVisibility[info.text] = info.visibility;
    });
    columnVisibilityState.value = currentVisibility;
    table.value?.setColumnVisibility(currentVisibility);
    columnSettingList.value = columnVisibilitySettings;

    const envData = getEnv(envKey.value);
    if (isChangedEnv(envData?.columnVisibility, currentVisibility)) {
      await saveEnv(envKey.value, {
        ...envData,
        columnVisibility: currentVisibility,
      });
    }

    emit('change-column-visibility', currentVisibility);
  };

  const hideColumn = (column: GridColumnInstance<RowDataType>): void => {
    column.toggleVisibility(false);
    const currentVisibility = { ...columnVisibilityState.value };
    currentVisibility[column.id] = false;
    columnVisibilityState.value = currentVisibility;
    columnSettingList.value.forEach((info) => {
      info.visibility = currentVisibility[info.text];
    });

    const envData = getEnv(envKey.value);
    if (isChangedEnv(envData?.columnVisibility, currentVisibility)) {
      saveEnv(envKey.value, {
        ...envData,
        columnVisibility: currentVisibility,
      });
    }

    emit('change-column-visibility', currentVisibility);
  };

  const updateColumnVisibilityState = async (
    updater: VisibilityState | ((state: VisibilityState) => VisibilityState),
  ) => {
    const newVisibility =
      typeof updater === 'function' ? updater(columnVisibilityState.value) : updater;

    if (isEqual(columnVisibilityState.value, newVisibility)) {
      return;
    }

    columnVisibilityState.value = newVisibility;

    const envData = getEnv(envKey.value);
    if (isChangedEnv(envData?.columnVisibility, newVisibility)) {
      await saveEnv(envKey.value, {
        ...envData,
        columnVisibility: newVisibility,
      });
    }

    emit('change-column-visibility', newVisibility);
  };

  const columnVisibilityOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => ({
    onColumnVisibilityChange: updateColumnVisibilityState,
  }));

  const setColumnVisibilityState = () => {
    const envData = getEnv(envKey.value);
    const { columnVisibility: envColumnVisibility } = envData ?? {};
    if (existObjectValue(envColumnVisibility)) {
      // env에 매핑이 없는 컬럼(예: 런타임에 추가된 동적 컬럼)은
      // columnSettingList의 코드 정의 기본값으로 채워, 모달 체크박스와 실제 visibility 상태의 불일치를 방지
      const mergedVisibility: VisibilityState = { ...envColumnVisibility };
      columnSettingList.value.forEach((item) => {
        if (!(item.text in mergedVisibility)) {
          mergedVisibility[item.text] = item.visibility;
        }
      });

      columnVisibilityState.value = mergedVisibility;
      columnSettingList.value.forEach((item) => {
        item.visibility = mergedVisibility[item.text];
      });
      table.value?.setColumnVisibility(mergedVisibility);
    }
  };

  return {
    columnVisibilityInfo,
    columnVisibilityState,
    columnVisibilityOptions,
    setColumnVisibility,
    hideColumn,
    setColumnVisibilityState,
  };
};
